<template>
  <div class="list">
    <div class="breadcrumb">
      <Breadcrumb>
        <Breadcrumb-item>老师管理</Breadcrumb-item>
        <Breadcrumb-item>在职老师请假率</Breadcrumb-item>
      </Breadcrumb>
    </div>
    <div class="search" @keyup.enter="search">
      <Form :label-width="100" :show-message="false" label-position="right">
        <Row :gutter="32">
          <Col :span="8">
            <Form-item label="统计月份">
              <Date-picker
                type="month"
                format="yyyy-MM"
                placeholder="选择月份"
                v-model="monthDate"
                :clearable="false"
                style="width: 100%;"
                @on-change="onMonthChange"
              ></Date-picker>
            </Form-item>
          </Col>
          <Col :span="8">
            <Form-item label="老师ID">
              <Input v-model="queryParam.tchId" placeholder="精确匹配"></Input>
            </Form-item>
          </Col>
          <Col :span="8">
            <Form-item label="老师姓名">
              <Input v-model="queryParam.tchName" placeholder="支持模糊搜索"></Input>
            </Form-item>
          </Col>
        </Row>
        <Row :gutter="32">
          <Col :span="8">
            <Form-item label="统计指标">
              <Select v-model="queryParam.rateType" transfer>
                <Option value="leave">请假率</Option>
                <Option value="late">迟到率</Option>
                <Option value="early">早退率</Option>
              </Select>
            </Form-item>
          </Col>
        </Row>
      </Form>
    </div>
    <div class="btn-container">
      <Row>
        <Button type="primary" :loading="searchLoading" icon="search" @click="search">搜索</Button>
        <Button style="margin-left: 8px;" @click="reset">重置</Button>
        <Button style="margin-left: 8px;" :loading="exportLoading" @click="exportList">导出</Button>
      </Row>
    </div>
    <div class="list-table">
      <div class="rate-formula">{{ rateFormula }}</div>
      <Table :key="tableSortKey" :columns="columns" :data="dataList" :loading="searchLoading" stripe @on-sort-change="onSortChange"></Table>
      <div style="margin-top: 12px; text-align: right;">
        <Page
          :total="totalCount"
          :current="queryParam.pageIndex"
          :page-size="queryParam.pageSize"
          show-total
          show-sizer
          @on-change="onPageChange"
          @on-page-size-change="onPageSizeChange"
        ></Page>
      </div>
    </div>
    <Modal
      v-model="showDetailModal"
      :title="detailModalTitle"
      width="880"
      :mask-closable="true"
      transfer>
      <Table :columns="detailColumns" :data="detailList" :loading="detailLoading" border stripe></Table>
      <div style="margin-top: 12px; text-align: right;">
        <Page
          :total="detailTotal"
          :current="detailPageIndex"
          :page-size="detailPageSize"
          show-total
          show-sizer
          @on-change="onDetailPageChange"
          @on-page-size-change="onDetailPageSizeChange"
        ></Page>
      </div>
      <div slot="footer">
        <Button :loading="detailExportLoading" @click="exportDetail">导出</Button>
        <Button type="primary" @click="showDetailModal=false">关闭</Button>
      </div>
    </Modal>
    <tch-info-modal
      :value="showTchInfoModal"
      @on-close="showTchInfoModal=false"
      :tchId="tchInfoId"
    ></tch-info-modal>
  </div>
</template>
<script>
import TchInfoModal from './modal/TchInfoModal.vue'
import { fetchTchScoreMap, applyTchScoresToRows, renderTchNameWithScore } from '@/utils/tchScore'
function pad (n) {
  return n < 10 ? '0' + n : '' + n
}
function monthRange (d) {
  let y = d.getFullYear()
  let m = d.getMonth()
  let start = y + '-' + pad(m + 1) + '-01'
  let endDay = new Date(y, m + 1, 0).getDate()
  let end = y + '-' + pad(m + 1) + '-' + pad(endDay)
  return { startDate: start, endDate: end }
}
let RATE_KIND = {
  leave: {
    label: '请假率',
    numerator: '请假课节',
    denominator: '正常上课课节',
    metric: 'leaveRate',
    formula: '请假率：请假率 = 全部请假类型或老师缺席的课 ÷ 有课课节。分子是全部请假类型，或通话里老师缺席，不看迟到、早退。没有分母时显示 —。'
  },
  late: {
    label: '迟到率',
    numerator: '迟到课节',
    denominator: '正常上课课节',
    metric: 'lateRate',
    formula: '迟到率：迟到率 = 老师迟到的课 ÷ 正常上课的课。正常上课是指这节课已经上完，学生没有缺席，老师也没有缺席。分子只算这些课里面记了老师迟到的。一节课只算一次。没有通话记录时，按学生和老师都没缺席、老师也没迟到来算。没有正常上课课节时显示 —。'
  },
  early: {
    label: '早退率',
    numerator: '早退课节',
    denominator: '正常上课课节',
    metric: 'earlyRate',
    formula: '早退率：早退率 = 老师早退的课 ÷ 正常上课的课。分母和迟到率一样：已经上完，并且学生、老师都没有缺席。分子只算这些课里面记了老师早退的。一节课只算一次。没有通话记录时，按都没缺席、老师也没早退来算。没有正常上课课节时显示 —。'
  }
}
function rateKind (type) {
  return RATE_KIND[type] || RATE_KIND.leave
}
function detailFieldTitle (type, field) {
  let kind = rateKind(type)
  let name = field === 'numerator' ? kind.numerator : kind.denominator
  return name + '明细'
}
export default {
  name: 'TchLeaveRateList',
  components: { TchInfoModal },
  data () {
    let now = new Date()
    let range = monthRange(now)
    return {
      monthDate: now,
      searchLoading: false,
      rankScoreSeq: 0,
      exportLoading: false,
      showTchInfoModal: false,
      tchInfoId: null,
      dataList: [],
      totalCount: 0,
      searchedRateType: 'leave',
      tableSortKey: 0,
      queryParam: {
        pageIndex: 1,
        pageSize: 20,
        startDate: range.startDate,
        endDate: range.endDate,
        tchId: '',
        tchName: '',
        rateType: 'leave'
      },
      showDetailModal: false,
      detailLoading: false,
      detailExportLoading: false,
      detailList: [],
      detailTotal: 0,
      detailPageIndex: 1,
      detailPageSize: 20,
      detailTchId: null,
      detailTchName: '',
      detailField: '',
      detailRateType: 'leave',
      detailColumns: [
        { title: '课节ID', key: 'lessonId', align: 'center', minWidth: 110 },
        { title: '老师ID', key: 'tchId', align: 'center', minWidth: 90 },
        { title: '学生ID', key: 'uid', align: 'center', minWidth: 90 },
        { title: '上课时间', key: 'occurTime', align: 'center', minWidth: 170 },
        { title: '类型', key: 'extra', align: 'center', minWidth: 140 }
      ]
    }
  },
  computed: {
    rateFormula () {
      return rateKind(this.searchedRateType).formula
    },
    columns () {
      let self = this
      let kind = rateKind(this.searchedRateType)
      return [
        {
          title: '老师',
          key: 'tchId',
          minWidth: 200,
          render: (h, params) => {
            let row = params.row || {}
            let id = row.tchId == null ? '' : String(row.tchId)
            let name = row.nickname || ''
            let label = id
            if (name) {
              label = id ? (id + ' ' + name) : name
            }
            if (!id) {
              return h('span', label || '—')
            }
            return renderTchNameWithScore(h, {
              text: label,
              score: row.monthRankScore,
              onClick: () => self.openTchInfoModal(row.tchId)
            })
          }
        },
        {
          title: kind.label,
          key: 'leaveRate',
          width: 120,
          sortable: 'custom',
          sortType: self.queryParam.sortProperty === 'leaveRate' ? self.queryParam.sortDirection : ''
        },
        {
          title: kind.numerator,
          key: 'leaveNumerator',
          width: 120,
          sortable: 'custom',
          sortType: self.queryParam.sortProperty === 'leaveNumerator' ? self.queryParam.sortDirection : '',
          render: (h, params) => {
            let val = params.row.leaveNumerator
            let num = val == null ? 0 : Number(val)
            if (num > 0) {
              return h('a', {
                attrs: { href: 'javascript:;' },
                on: {
                  click: () => {
                    self.openDetail(params.row, 'numerator')
                  }
                }
              }, String(num))
            }
            return h('span', String(num))
          }
        },
        {
          title: kind.denominator,
          key: 'leaveDenominator',
          width: 140,
          render: (h, params) => {
            let val = params.row.leaveDenominator
            let num = val == null ? 0 : Number(val)
            if (num > 0) {
              return h('a', {
                attrs: { href: 'javascript:;' },
                on: {
                  click: () => {
                    self.openDetail(params.row, 'denominator')
                  }
                }
              }, String(num))
            }
            return h('span', String(num))
          }
        }
      ]
    },
    detailModalTitle () {
      let fieldName = detailFieldTitle(this.detailRateType, this.detailField)
      let name = this.detailTchName || ''
      let id = this.detailTchId == null ? '' : String(this.detailTchId)
      let who = name ? (name + '（' + id + '）') : id
      return who ? (who + ' · ' + fieldName) : fieldName
    }
  },
  mounted () {
    this.search()
  },
  methods: {
    onMonthChange (val) {
      let d = val ? new Date(val + '-01') : new Date()
      if (isNaN(d.getTime())) {
        d = new Date()
      }
      this.monthDate = d
      let range = monthRange(d)
      this.queryParam.startDate = range.startDate
      this.queryParam.endDate = range.endDate
    },
    reset () {
      let now = new Date()
      this.monthDate = now
      let range = monthRange(now)
      this.queryParam = {
        pageIndex: 1,
        pageSize: 20,
        startDate: range.startDate,
        endDate: range.endDate,
        tchId: '',
        tchName: '',
        rateType: 'leave',
        sortProperty: '',
        sortDirection: ''
      }
      this.tableSortKey = this.tableSortKey + 1
      this.search()
    },
    onSortChange (column) {
      let nextProp = ''
      let nextDir = ''
      if (column && column.order && column.order !== 'normal') {
        nextProp = column.key || ''
        nextDir = column.order
      }
      if ((this.queryParam.sortProperty || '') === nextProp && (this.queryParam.sortDirection || '') === nextDir) {
        return
      }
      this.queryParam.sortProperty = nextProp
      this.queryParam.sortDirection = nextDir
      this.queryParam.pageIndex = 1
      this.search(false)
    },
    onPageChange (page) {
      this.queryParam.pageIndex = page
      this.search(false)
    },
    onPageSizeChange (size) {
      this.queryParam.pageSize = size
      this.queryParam.pageIndex = 1
      this.search(false)
    },
    buildListParams (withPage) {
      let params = {
        startDate: this.queryParam.startDate,
        endDate: this.queryParam.endDate,
        rateType: this.queryParam.rateType || 'leave'
      }
      if (withPage !== false) {
        params.pageIndex = this.queryParam.pageIndex
        params.pageSize = this.queryParam.pageSize
      }
      if (this.queryParam.tchId) {
        params.tchId = Number(this.queryParam.tchId)
      }
      if (this.queryParam.tchName) {
        params.tchName = this.queryParam.tchName
      }
      if (this.queryParam.sortProperty && this.queryParam.sortDirection) {
        params.sortProperty = this.queryParam.sortProperty
        params.sortDirection = this.queryParam.sortDirection
      }
      return params
    },
    search (resetPage) {
      if (resetPage !== false) {
        this.queryParam.pageIndex = 1
      }
      let self = this
      let params = self.buildListParams()
      let rateType = params.rateType
      self.searchLoading = true
      self.$http.post('/tch/leave-rate/list', params).then(function (res) {
        self.searchLoading = false
        self.searchedRateType = rateType
        if (res && res.status === 'ok' && res.data) {
          self.dataList = res.data.data || []
          self.totalCount = res.data.totalCount || 0
          self.loadMonthRankScores()
        } else {
          self.dataList = []
          self.totalCount = 0
        }
      }).catch(function () {
        self.searchLoading = false
      })
    },
    loadMonthRankScores () {
      const rows = this.dataList || []
      if (!rows.length) {
        return
      }
      const ids = rows.map(function (row) { return row.tchId }).filter(function (id) { return id != null })
      const seq = ++this.rankScoreSeq
      const self = this
      fetchTchScoreMap(this.$http, ids).then(function (map) {
        if (seq !== self.rankScoreSeq) {
          return
        }
        applyTchScoresToRows(self, rows, 'tchId', 'monthRankScore', map)
      })
    },
    exportList () {
      let self = this
      if (!self.queryParam.startDate || !self.queryParam.endDate) {
        self.$Message.error('请先选择统计月份')
        return
      }
      let params = self.buildListParams(false)
      let kind = rateKind(params.rateType)
      let file = '在职老师' + kind.label + '_' + self.queryParam.startDate + '_' + self.queryParam.endDate + '.xlsx'
      self.exportLoading = true
      self.downloadExcel('/tch/leave-rate/list-export', params, file).finally(function () {
        self.exportLoading = false
      })
    },
    openTchInfoModal (tchId) {
      this.tchInfoId = tchId
      this.showTchInfoModal = true
    },
    openDetail (row, field) {
      if (!row || !row.tchId) {
        return
      }
      this.detailTchId = row.tchId
      this.detailTchName = row.nickname || ''
      this.detailField = field
      this.detailRateType = this.searchedRateType || 'leave'
      this.detailPageIndex = 1
      this.detailList = []
      this.detailTotal = 0
      this.showDetailModal = true
      this.loadDetail()
    },
    onDetailPageChange (page) {
      this.detailPageIndex = page
      this.loadDetail()
    },
    onDetailPageSizeChange (size) {
      this.detailPageSize = size
      this.detailPageIndex = 1
      this.loadDetail()
    },
    loadDetail () {
      let self = this
      if (!self.detailTchId || !self.detailField || !self.queryParam.startDate || !self.queryParam.endDate) {
        self.detailList = []
        self.detailTotal = 0
        return
      }
      let kind = rateKind(self.detailRateType)
      self.detailLoading = true
      self.$http.post('/tch/hr-metrics-trend-detail', {
        metric: kind.metric,
        field: self.detailField,
        startDate: self.queryParam.startDate,
        endDate: self.queryParam.endDate,
        tchId: self.detailTchId,
        pageIndex: self.detailPageIndex,
        pageSize: self.detailPageSize
      }).then(function (res) {
        if (res && res.status === 'ok' && res.data) {
          self.detailList = res.data.data || []
          self.detailTotal = res.data.totalCount || 0
        } else {
          self.detailList = []
          self.detailTotal = 0
          self.$Message.error((res && res.msg) || '明细加载失败')
        }
      }).catch(function () {
        self.detailList = []
        self.detailTotal = 0
        self.$Message.error('明细加载失败')
      }).finally(function () {
        self.detailLoading = false
      })
    },
    exportDetail () {
      let self = this
      if (!self.detailTchId || !self.detailField || !self.queryParam.startDate || !self.queryParam.endDate) {
        self.$Message.error('请先打开课节明细')
        return
      }
      let kind = rateKind(self.detailRateType)
      let fieldName = detailFieldTitle(self.detailRateType, self.detailField)
      let file = fieldName + '_' + self.detailTchId + '_' + self.queryParam.startDate + '_' + self.queryParam.endDate + '.xlsx'
      self.detailExportLoading = true
      self.downloadExcel('/tch/hr-metrics-trend-detail-export', {
        metric: kind.metric,
        field: self.detailField,
        startDate: self.queryParam.startDate,
        endDate: self.queryParam.endDate,
        tchId: self.detailTchId
      }, file).finally(function () {
        self.detailExportLoading = false
      })
    },
    downloadExcel (url, data, filename) {
      let self = this
      return self.$http({
        method: 'post',
        url: url,
        data: data,
        responseType: 'arraybuffer'
      }).then(function (response) {
        if (response && response.data) {
          const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
          const link = document.createElement('a')
          link.target = '_blank'
          link.href = window.URL.createObjectURL(blob)
          link.download = filename
          link.click()
          window.URL.revokeObjectURL(link.href)
        } else {
          self.$Message.error('导出失败')
        }
      }).catch(function () {
        self.$Message.error('导出失败')
      })
    }
  }
}
</script>
<style scoped>
.rate-formula {
  margin-bottom: 12px;
  line-height: 22px;
  color: #515a6e;
}
</style>



// WEBPACK FOOTER //
// TchLeaveRateList.vue?c8dce0f4